import { useState, useEffect, useCallback, useRef } from 'react';
import { SECOND } from 'core/common/constants';

type EasingMode = 'linear' | 'slowStartFastFinish' | 'fastStartSlowFinish' | 'slowMiddle';

type Params = {
  duration: number;
  mode?: EasingMode;
  onComplete?: () => void;
};

const easingFunctions = {
  linear: (t: number) => t,
  slowStartFastFinish: (t: number) => t * t,
  fastStartSlowFinish: (t: number) => 1 - Math.pow(1 - t, 3),
  slowMiddle: (t: number) => {
    // Fast (0-30%)
    if (t < 0.3) {
      return (t / 0.3) * 0.3;
    }

    // Slow (30-70%)
    if (t < 0.7) {
      return 0.3 + ((t - 0.3) * 0.4) / 0.4;
    }

    // Fast (70-100%)
    const remaining = (t - 0.7) / 0.3;
    return 0.7 + remaining * remaining * 0.3;
  },
};

export const useProgressWithRandomization = ({
  duration,
  mode = 'slowMiddle',
  onComplete,
}: Params) => {
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const elapsedBeforePause = useRef(0);
  const isCompletedRef = useRef(false);

  const cleanupAnimation = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  }, []);

  const easeProgress = useCallback(
    (normalizedTime: number): number => {
      const easingFunction = easingFunctions[mode] || easingFunctions.linear;
      return easingFunction(normalizedTime);
    },
    [mode],
  );

  const totalDuration = duration * SECOND;

  const animate = useCallback(
    (currentTime: number) => {
      if (isCompletedRef.current) return;

      if (startTimeRef.current === null) {
        startTimeRef.current = currentTime;
      }

      const elapsedTime = elapsedBeforePause.current + (currentTime - startTimeRef.current);
      const normalizedTime = Math.min(elapsedTime / totalDuration, 1);

      const easedProgress = easeProgress(normalizedTime);
      const progressValue = Math.round(easedProgress * 100);
      setProgress(progressValue);

      if (normalizedTime < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        isCompletedRef.current = true;
        cleanupAnimation();
        onComplete?.();
      }
    },
    [easeProgress, onComplete, totalDuration, cleanupAnimation],
  );

  useEffect(() => {
    if (!isPaused && !isCompletedRef.current) {
      animationRef.current = requestAnimationFrame(animate);
    }

    return cleanupAnimation;
  }, [animate, isPaused, cleanupAnimation]);

  const pause = useCallback(() => {
    if (!isPaused && !isCompletedRef.current) {
      setIsPaused(true);
      cleanupAnimation();

      if (startTimeRef.current !== null) {
        const now = performance.now();
        elapsedBeforePause.current += now - startTimeRef.current;
      }

      startTimeRef.current = null;
    }
  }, [isPaused, cleanupAnimation]);

  const resume = useCallback(() => {
    if (isPaused && !isCompletedRef.current) {
      setIsPaused(false);
      startTimeRef.current = performance.now();
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [isPaused, animate]);

  return { progress, pause, resume, isPaused };
};
